import React, { useState } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  CircularProgress,
  Snackbar,
  Alert,
  SelectChangeEvent,
  InputAdornment,
  IconButton
} from '@mui/material';
import { PersonAdd, Visibility, VisibilityOff } from '@mui/icons-material';
import { AdminUserRegistration } from '../../interfaces/auth';
import { useAuth } from '../../contexts/AuthContext';
import { styles } from './styles';
import { darkModeTextField } from '../../styles/textFieldStyles';
import { darkModeSelect } from '../../styles/selectStyles';

interface UserRegistrationFormProps {
  onUserRegistered?: () => void;
}

const UserRegistrationForm: React.FC<UserRegistrationFormProps> = ({ onUserRegistered }) => {
  const { registerUser } = useAuth();
  
  const [formData, setFormData] = useState<AdminUserRegistration>({
    email: '',
    password: '',
    role: 'Default',
  });
  
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string>('Usuário registrado com sucesso!');
  const [showPassword, setShowPassword] = useState(false);
  
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };
  
  const handleSelectChange = (e: SelectChangeEvent) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };
  
  const toggleShowPassword = () => {
    setShowPassword(!showPassword);
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    
    try {
      // Pegar o nome de usuário da resposta da API (se disponível na mensagem)
      const response = await registerUser(formData);
      setSuccess(true);
      
      // Extrair o nome de usuário da mensagem de resposta se disponível
      if (response && response.message) {
        setSuccessMessage(response.message);
      }
      
      // Chamar o callback onUserRegistered, se fornecido
      if (onUserRegistered) {
        onUserRegistered();
      }
      
      // Reset form
      setFormData({
        email: '',
        password: '',
        role: 'Default',
      });
    } catch (err) {
      if (err instanceof Error) {
        setError(err.message);
      } else {
        setError('Erro ao registrar usuário');
      }
    } finally {
      setLoading(false);
    }
  };
  
  const handleCloseSnackbar = () => {
    setSuccess(false);
  };
  
  return (
    <Box sx={styles.container}>
      <Paper elevation={3} sx={styles.paper}>
        <Box sx={styles.header}>
          <PersonAdd sx={styles.icon} />
          <Typography variant="h5" sx={styles.title}>
            Registrar Novo Usuário
          </Typography>
        </Box>
        
        <Typography variant="body2" sx={styles.subtitle}>
          Preencha os campos abaixo para criar um novo usuário. O usuário precisará completar o registro no primeiro login.
        </Typography>
        
        <Box component="form" onSubmit={handleSubmit} sx={styles.form}>
          <TextField
            label="E-mail"
            name="email"
            type="email"
            value={formData.email}
            onChange={handleInputChange}
            required
            fullWidth
            margin="normal"
            variant="outlined"
            sx={darkModeTextField}
          />
          
          <TextField
            label="Senha"
            name="password"
            type={showPassword ? 'text' : 'password'}
            value={formData.password}
            onChange={handleInputChange}
            required
            fullWidth
            margin="normal"
            variant="outlined"
            helperText="Mínimo de 6 caracteres"
            sx={darkModeTextField}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={toggleShowPassword}
                    edge="end"
                    sx={{ color: 'rgba(255, 255, 255, 0.7)' }}
                  >
                    {showPassword ? <Visibility /> : <VisibilityOff />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          
          <FormControl fullWidth margin="normal" sx={darkModeSelect}>
            <InputLabel id="role-label">Tipo de Usuário</InputLabel>
            <Select
              labelId="role-label"
              name="role"
              value={formData.role}
              onChange={handleSelectChange}
              label="Tipo de Usuário"
            >
              <MenuItem value="Default">Padrão</MenuItem>
              <MenuItem value="Admin">Administrador</MenuItem>
            </Select>
          </FormControl>
          
          {error && (
            <Typography color="error" sx={styles.error}>
              {error}
            </Typography>
          )}
          
          <Button
            type="submit"
            variant="contained"
            color="primary"
            fullWidth
            sx={styles.submitButton}
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : 'Registrar Usuário'}
          </Button>
        </Box>
      </Paper>
      
      <Snackbar open={success} autoHideDuration={6000} onClose={handleCloseSnackbar}>
        <Alert onClose={handleCloseSnackbar} severity="success" sx={{ width: '100%' }}>
          {successMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default UserRegistrationForm;